import { MetadataResponse } from "../api/account";
import { AuthUser, LicenseStatus } from "./types";

function fmtTimestamp(ms?: string | number): string {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString(undefined, { month: "short" });
  const year = d.getFullYear();
  return `${day}-${mon}-${year}`;
}

/** Epoch milliseconds -> "05-Aug-2026 14:30". */
function fmtLastLogin(ms?: number): string {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString(undefined, { month: "short" });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${day}-${mon}-${year} ${time}`;
}

function licenseStatus(expiryMs?: string | number): LicenseStatus {
  if (!expiryMs) return "Unknown";
  return Date.now() < Number(expiryMs) ? "Active" : "Expired";
}

function initials(displayName?: string, userId?: string): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  // Fall back to the first two chars of the email local part.
  return (userId || "??").split("@")[0].slice(0, 2).toUpperCase();
}

/** Shape a `/metadata` response into the user we persist and render. */
export function mapResponse(
  data: MetadataResponse,
  uid: string,
  pin: string
): AuthUser {
  const u = data.User ?? {};
  const um = u.Metadata || {};
  const expiry = um.purchaseexpiry;
  // Accounts often have no DisplayName, in which case the business reads far
  // better than the raw login email.
  const displayName = u.DisplayName || um.businessname;

  return {
    id: u.Id,
    name: displayName || u.UserId || uid,
    email: u.UserId || uid,
    avatar: initials(displayName, u.UserId),
    registeredDate: u.CreatedDate || "—",
    purchaseDate: fmtTimestamp(um.purchasedate),
    expiryDate: fmtTimestamp(expiry),
    licenseStatus: licenseStatus(expiry),
    licenseId: um.purchasemode || "—",
    devicesUsed: parseInt(um.devicecount ?? "", 10) || 0,
    lastLogin: fmtLastLogin(u.RecentLoginTimestamp),
    // The app version fields live on the user's metadata, not the top-level
    // Metadata block (which holds row counts).
    rawMeta: um,
    _uid: uid,
    _pin: pin,
  };
}
